import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Card,
  Form,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { BASE_URL, HEADERS, getStatusDotColor } from "../../../utils/constant";
import { FaFileExport } from "react-icons/fa";
import ExportModal from "../../Modals/ExportsModel/main";
import NoData from "../../NoData";

const AttendanceModal = ({
  show,
  handleClose,
  technician,
  fetchTechnicians,
  fetchStatusCounts,
}) => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [singleDate, setSingleDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [newPunchIn, setNewPunchIn] = useState("");
  const [newPunchOut, setNewPunchOut] = useState("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const userPermissions = localStorage.getItem("user_permissions");
  useEffect(() => {
    if (technician && show) {
      fetchAttendanceData();
    }
  }, [technician, show]);

  const fetchAttendanceData = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("user_id", technician.id);

      if (filters.fromDate && filters.toDate) {
        params.append("from_date", filters.fromDate);
        params.append("to_date", filters.toDate);
      } else if (filters.singleDate) {
        params.append("date", filters.singleDate);
      }

      const response = await axios.get(
        `${BASE_URL}/users/attendance/?${params}`,
        { headers: HEADERS },
      );

      setAttendanceData(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    if (singleDate) {
      fetchAttendanceData({ singleDate });
    } else if (fromDate && toDate) {
      fetchAttendanceData({ fromDate, toDate });
    } else {
      toast.info("Please select From–To or a single date");
    }
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setSingleDate("");
    fetchAttendanceData();
  };

  const formatTime = (datetime) => {
    if (!datetime) return "--";
    return moment(datetime).format("HH:mm:ss");
  };

  const handleViewLogs = (item) => {
    setSelectedLogs(item.logs || []);
    setSelectedDate(item.date);
    setShowLogsModal(true);
  };

  const handleOpenUpdate = (record) => {
    setSelectedRecord(record);
    setNewStatus(record.status || "");

    const punchInTime = record.latest_punch_in
      ? record.latest_punch_in.slice(11, 16)
      : "";
    const punchOutTime = record.latest_punch_out
      ? record.latest_punch_out.slice(11, 16)
      : "";

    setNewPunchIn(punchInTime);
    setNewPunchOut(punchOutTime);
    setShowUpdateModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!newStatus && !newPunchIn && !newPunchOut) {
      toast.info("Please update at least one field");
      return;
    }

    try {
      setLoading(true);

      const recordDate = selectedRecord.date
        ? new Date(selectedRecord.date)
        : new Date();

      const formatDateTime = (time) => {
        if (!time) return null;

        const [hours, minutes] = time.split(":");
        const yyyy = recordDate.getFullYear();
        const mm = String(recordDate.getMonth() + 1).padStart(2, "0");
        const dd = String(recordDate.getDate()).padStart(2, "0");

        const hh = String(hours).padStart(2, "0");
        const min = String(minutes).padStart(2, "0");

        return `${yyyy}-${mm}-${dd}T${hh}:${min}:00`;
      };

      const payload = {
        status: newStatus,
        punch_in: formatDateTime(newPunchIn),
        punch_out: formatDateTime(newPunchOut),
      };

      await axios.put(
        `${BASE_URL}/users/attendance/${selectedRecord.id}/`,
        payload,
        { headers: HEADERS },
      );

      toast.success("Attendance updated successfully");
      setShowUpdateModal(false);
      fetchAttendanceData();
      fetchStatusCounts();
      fetchTechnicians();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Attendance — {technician?.first_name} {technician?.last_name}
        </Modal.Title>
        <Button
          variant="primary"
          onClick={() => setShowExportModal(true)}
          className="ms-auto d-flex align-items-center"
        >
          <FaFileExport className="me-2" /> Export
        </Button>
      </Modal.Header>

      <Modal.Body>
        <Card className="shadow-sm border-0 mb-3">
          <Card.Body>
            <Form>
              <Row className="g-3 align-items-end">
                <Col md={3} sm={6}>
                  <Form.Label className="fw-semibold small text-muted">
                    From
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setSingleDate("");
                    }}
                  />
                </Col>

                <Col md={3} sm={6}>
                  <Form.Label className="fw-semibold small text-muted">
                    To
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setSingleDate("");
                    }}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label className="fw-semibold small text-muted">
                    Single Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={singleDate}
                    onChange={(e) => {
                      setSingleDate(e.target.value);
                      setFromDate("");
                      setToDate("");
                    }}
                  />
                </Col>

                <Col md={3} className="d-flex">
                  <Button
                    className="me-2 px-3 py-1"
                    variant="primary"
                    onClick={handleFilter}
                  >
                    Apply
                  </Button>
                  <Button
                    className="px-3 py-1"
                    variant="outline-secondary"
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" />
          </div>
        ) : attendanceData.length === 0 ? (
          <NoData message="No Attendance Data Found" height="300px" />
        ) : (
          <div
            style={{
              overflowY: "auto",
              maxHeight: "450px",
              borderRadius: "10px",
              background: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            <Table
              hover
              responsive
              bordered={false}
              className="align-middle mb-0"
              style={{
                fontSize: "0.9rem",
                borderCollapse: "separate",
                borderSpacing: "0 6px",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f8f9fa",
                  zIndex: 2,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.85rem",
                }}
                className="text-center text-secondary"
              >
                <tr>
                  <th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Punch In / Out</th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>

              <tbody className="text-center">
                {attendanceData.length > 0 ? (
                  attendanceData.map((record, index) => {
                    const punchIn = record.latest_punch_in
                      ? formatTime(record.latest_punch_in)
                      : "--";
                    const punchOut = record.latest_punch_out
                      ? formatTime(record.latest_punch_out)
                      : "--";

                    return (
                      <tr
                        key={index}
                        style={{
                          backgroundColor: "#ffffff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          borderRadius: "8px",
                        }}
                        className="mx-2 my-1"
                      >
                        <td className="py-2 px-3 text-nowrap">
                          {moment(record.date).format("DD MMM YYYY")}
                        </td>
                        <td className="py-2 px-3">
                          <Badge
                            bg=""
                            style={{
                              backgroundColor: getStatusDotColor(record.status),
                              color: "#fff",
                              textTransform: "capitalize",
                              fontSize: "0.8rem",
                              padding: "6px 10px",
                              borderRadius: "20px",
                            }}
                          >
                            {record.status || "—"}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-nowrap text-muted">
                          {punchIn} <span className="text-secondary"> - </span>{" "}
                          {punchOut}
                        </td>
                        <td className="py-2 px-3 text-nowrap">
                          <div className="d-flex justify-content-center flex-wrap gap-2">
                            {record.logs?.length >= 1 && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="text-decoration-none"
                                onClick={() => handleViewLogs(record)}
                                style={{
                                  borderRadius: "20px",
                                  padding: "4px 10px",
                                  fontSize: "0.8rem",
                                }}
                              >
                                View Logs
                              </Button>
                            )}
                            {userPermissions.includes(
                              "attendance_change_attendance",
                            ) && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleOpenUpdate(record)}
                                style={{
                                  borderRadius: "20px",
                                  padding: "4px 12px",
                                  fontSize: "0.8rem",
                                }}
                              >
                                Update
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="text-muted py-4">
                      No attendance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>

      <Modal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 mb-0">
            All Logs — {moment(selectedDate).format("DD MMM YYYY")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {selectedLogs.length ? (
            <ul className="mb-0 bg-light p-2 rounded">
              {Array.from({ length: Math.ceil(selectedLogs.length / 2) }).map(
                (_, i) => {
                  const inLog = selectedLogs[i * 2];
                  const outLog = selectedLogs[i * 2 + 1];
                  const inTime = inLog
                    ? moment(inLog).format("HH:mm:ss")
                    : "--";
                  const outTime = outLog
                    ? moment(outLog).format("HH:mm:ss")
                    : "--";
                  return (
                    <li
                      key={i}
                      style={{ padding: "4px 0", fontSize: "0.9rem" }}
                    >
                      {inTime} <span className="text-muted"> - </span> {outTime}
                    </li>
                  );
                },
              )}
            </ul>
          ) : (
            <div className="text-center text-muted small">
              No logs available
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        centered
        size="sm"
      >
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small text-muted">
              Status
            </Form.Label>
            <Form.Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="present">Present</option>
              <option value="idle">Idle</option>
              <option value="absent">Absent</option>
              <option value="onleave">Leave</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small text-muted">
              Punch In
            </Form.Label>
            <Form.Control
              type="time"
              value={newPunchIn}
              onChange={(e) => setNewPunchIn(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-semibold small text-muted">
              Punch Out
            </Form.Label>
            <Form.Control
              type="time"
              value={newPunchOut}
              onChange={(e) => setNewPunchOut(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowUpdateModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpdateStatus}
            disabled={!newStatus || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {showExportModal && (
        <ExportModal
          show={showExportModal}
          handleClose={() => setShowExportModal(false)}
          title="attendence"
          attendanceid={technician.id}
        />
      )}
    </Modal>
  );
};

export default AttendanceModal;
